// @mui
import { Grid, Skeleton, Typography, Box, Chip } from "@mui/material";
// components
import GeoGraph from "./Map";
import CountryDetails from "./CountryDetails";
import ContentBox from "~src/components/ContentBox";
//utils
import { CountryMapData } from "~src/types/brandMapping";

export type MarketCategory = { id: string; value: number };
type MarketCategories = {
  established: MarketCategory[];
  growing: MarketCategory[];
  opportunity: MarketCategory[];
};

// ------------------------------------------------------------------------------------------------
export default function GeoMap({
  trafficCountries,
  loading,
  tabItem,
}: {
  trafficCountries: CountryMapData[] | undefined;
  loading: boolean;
  tabItem: { label: string; value: string; tooltip: string };
}) {
  function categorizeMarkets(): MarketCategories {
    const countryCount: Record<string, number> = {};

    // Populate the countryCount object with country and its count.
    trafficCountries?.forEach(([country, count]) => {
      countryCount[country] = count;
    });

    // Convert countryCount object into an array of objects with id (country) and value (count).
    const countryFrequencies: MarketCategory[] = Object.keys(countryCount).map(
      (country) => ({
        id: country,
        value: countryCount[country],
      }),
    );

    // Sort countries by their value (count) in descending order.
    countryFrequencies.sort((a, b) => b.value - a.value);

    // Get the minimum and maximum values to define thresholds.
    const minCount =
      countryFrequencies[countryFrequencies.length - 1]?.value || 0;
    const maxCount = countryFrequencies[0]?.value || 0;
    const range = maxCount - minCount;

    // Define thresholds by dividing the range into three parts.
    const establishedThreshold = maxCount - range * (1 / 3);
    const growingThreshold = maxCount - range * (2 / 3);

    const marketCategories: MarketCategories = {
      established: [],
      growing: [],
      opportunity: [],
    };

    // Categorize countries based on their count value.
    countryFrequencies.forEach((country) => {
      if (country.value >= establishedThreshold) {
        marketCategories.established.push(country); // Add full object
      } else if (country.value >= growingThreshold) {
        marketCategories.growing.push(country); // Add full object
      } else {
        marketCategories.opportunity.push(country); // Add full object
      }
    });

    return marketCategories;
  }

  // Function to calculate quantiles for log-transformed data
  return (
    <ContentBox my={3}>
      {!loading && (
        <Grid container>
          {
            <Grid  sx={{ height: 424 }} size={{ xs: 12, md: 8 }} >
              <GeoGraph graphData={categorizeMarkets()} />
            </Grid>
          }

          <Grid  size={{ xs: 12, md: 4 }} >
            <CountryDetails
              data={categorizeMarkets()}
              tooltip={tabItem.tooltip}
            />
          </Grid>
        </Grid>
      )}
      {loading && <Skeleton height={424} variant="rectangular" />}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "center" },
          flexWrap: "wrap",
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: 2,
            mb: { xs: 1, md: 0 },
          }}
        >
          <Chip
            sx={{
              height: "14px",
              width: "14px",
              borderRadius: "3px",
              mr: 1,
              backgroundColor: "#7AA5D3",
            }}
          />
          <Typography>Established</Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: 2,
            mb: { xs: 1, md: 0 },
          }}
        >
          <Chip
            sx={{
              height: "14px",
              width: "14px",
              borderRadius: "3px",
              mr: 1,
              backgroundColor: "#43D2E9",
            }}
          />
          <Typography>Growing</Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: 2,
            mb: { xs: 1, md: 0 },
          }}
        >
          <Chip
            sx={{
              height: "14px",
              width: "14px",
              borderRadius: "3px",
              mr: 1,
              backgroundColor: "#A24AF0",
            }}
          />
          <Typography>Opportunity</Typography>
        </Box>
      </Box>
    </ContentBox>
  );
}
